/** Phase 5E: lightweight single-guest add for Admin + Coordinator. */
import { useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import api, { formatErr } from "../lib/api";
import { UserPlus, X } from "lucide-react";
import { T } from "../lib/i18n";

export default function AddGuestButton({ onAdded }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ first_name: "", last_name: "", table: "", seat: "", dietary_notes: "", vip: false });
  const [dupWarning, setDupWarning] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target ? e.target.value : e }));
  const close = () => { setOpen(false); setDupWarning(null); setF({ first_name: "", last_name: "", table: "", seat: "", dietary_notes: "", vip: false }); };

  const save = async (allowDuplicate = false) => {
    if (!f.first_name.trim() || !f.last_name.trim()) return toast.error(T("First and last name are required"));
    setBusy(true);
    try {
      const { data } = await api.post("/admin/guests/add", { ...f, allow_duplicate: allowDuplicate });
      if (data.duplicate) {
        setDupWarning(data.message);
      } else {
        toast.success(`${data.guest.full_name} added — Table ${data.guest.table_name || "—"}`);
        close();
        onAdded && onAdded(data.guest);
      }
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} className="btn-primary text-xs px-4 py-2 flex items-center gap-1.5" data-testid="add-guest-btn">
        <UserPlus className="w-3.5 h-3.5" /> {T("+ Add Guest")}
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ background: "rgba(10,10,10,0.7)" }} data-testid="add-guest-dialog">
          <div className="card-elev p-5 w-full max-w-sm bg-white rounded-2xl" style={{ background: "var(--motif-surface)" }}>
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-xl text-motif">{T("Add Guest")}</div>
              <button onClick={close} className="w-8 h-8 rounded-full bg-accent-motif flex items-center justify-center" data-testid="add-guest-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input className="input-motif text-sm" placeholder={T("First Name")} value={f.first_name} onChange={set("first_name")} data-testid="add-guest-first" />
              <input className="input-motif text-sm" placeholder={T("Last Name")} value={f.last_name} onChange={set("last_name")} data-testid="add-guest-last" />
              <input className="input-motif text-sm" placeholder={T("Table")} value={f.table} onChange={set("table")} data-testid="add-guest-table" />
              <input className="input-motif text-sm" placeholder={T("Seat")} value={f.seat} onChange={set("seat")} data-testid="add-guest-seat" />
            </div>
            <input className="input-motif text-sm w-full mt-2" placeholder={T("Dietary Notes (optional)")} value={f.dietary_notes} onChange={set("dietary_notes")} data-testid="add-guest-dietary" />
            <label className="flex items-center gap-2 mt-3 text-sm text-motif cursor-pointer">
              <input type="checkbox" checked={f.vip} onChange={(e) => setF((s) => ({ ...s, vip: e.target.checked }))} data-testid="add-guest-vip" />
              VIP
            </label>
            {dupWarning && (
              <div className="mt-3 p-3 rounded-xl text-xs" style={{ background: "rgba(220,150,50,0.12)", border: "1px solid rgba(220,150,50,0.5)" }} data-testid="add-guest-dup-warning">
                {dupWarning}
                <button onClick={() => save(true)} disabled={busy} className="btn-ghost text-[11px] mt-2 block underline" data-testid="add-guest-dup-confirm">
                  {T("Add anyway")}
                </button>
              </div>
            )}
            <button onClick={() => save(false)} disabled={busy} className="btn-primary w-full mt-4" data-testid="add-guest-save">
              {busy ? T("Saving…") : T("Save Guest")}
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
